import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    Box,
    Drawer,
    Chip,
    Button,
    Avatar,
    Tooltip,
    CircularProgress,
    Skeleton,
    IconButton,
    Menu,
    MenuItem,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { IoClose, IoFilterSharp } from "react-icons/io5";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import TableChartIcon from '@mui/icons-material/TableChart';
import { MdOutlineSpaceDashboard } from "react-icons/md";
import { FaListUl } from "react-icons/fa6";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { GoPlus } from "react-icons/go";
import { IoFlagOutline } from "react-icons/io5";
import { BsThreeDots } from "react-icons/bs";
import { IoTimeOutline } from "react-icons/io5";
import { ImAttachment } from "react-icons/im";
import { LuMessageSquareText } from "react-icons/lu";
import filterIcon1 from "../../assets/images/filter1.svg";
import { VscRepoForked } from "react-icons/vsc";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "../../utils/debounce";
import dayjs from "dayjs";
import { getProject } from "../../services/project.service";
import { getTokenbyData, getUser } from "../../services/user.service";
import { useAuth } from "../../context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import PermissionGate from "../../components/Permission/PermissionGate";
import { usePermissions } from "../../context/PermissionProvider";
import InProgress_Icon from "../../assets/images/icon2.png";
import QA_Icon from "../../assets/images/icon3.png";
import Completed_Icon from "../../assets/images/icon1.png";
import bug from "../../assets/images/bug.png";
import Task_Icon from "../../assets/images/Task_Icon.svg";
import Meeting_Icon from "../../assets/images/Meeting.png";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import {
    deleteTask,
    getClientTaskList,
    getTask,
    getTaskById,
    getUserProject,
    updateTask,
} from "../../services/taskManagement.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import AddTaskModal from "../TaskManagement/AddTaskModal";
import TaskDrawer from "../TaskManagement/TaskDrawer";
import { Divider } from "antd";
import { MdOutlineEventNote } from "react-icons/md";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { STATUS, PRIORITY, ISSUETYPE } from "../../common/constant/constant";

dayjs.extend(require("dayjs/plugin/duration"));

const MemoizedTaskDrawer = React.memo(TaskDrawer);

// Create display arrays from constants
const statusOptions = [
    { value: STATUS.PENDING, label: "Pending" },
    { value: STATUS.TODO, label: "To Do" },
    { value: STATUS.INPROGRESS, label: "InProgress" },
    { value: STATUS.QA, label: "QA" },
    { value: STATUS.STAGING, label: "Staging" },
    { value: STATUS.UAT, label: "UAT" },
    { value: STATUS.PRODUCTION, label: "Production" },
];

const priorityOptions = [
    { value: PRIORITY.LOW, label: "Low" },
    { value: PRIORITY.MEDIUM, label: "Medium" },
    { value: PRIORITY.HIGH, label: "High" },
];

// Helper function to get label by value
const getLabelByValue = (value, options) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : "Unknown";
};

const BugList = ({
    tasks,
    tableTotalCount,
    setSelectedTask,
    setDrawerOpen,
    queryParam,
    loading,
}) => {
    const columns = [
        { id: "taskNo", label: "Bug No" },
        { id: "statusLabel", label: "Status" },
        { id: "title", label: "Bug" },
        { id: "priorityLabel", label: "Priority" },
        { id: "formattedActualDeadline", label: "Due Date" },
        { id: "formattedClientDeadline", label: "Client Due Date" },
        { id: "assignedToName", label: "Assignee" },
    ];

    const tableConfig = {
        isAction: false,
        isServerSide: true,
        isView: true,
        isEdit: false,
        isDelete: false,
        isSearch: false,
        isAddNew: false,
    };

    let data = [];
    for (const key in tasks) {
        data.push(...tasks[key]);
    }

    const priorityMap = {
        [PRIORITY.HIGH]: "#D50707",
        [PRIORITY.MEDIUM]: "#D7BB21",
        [PRIORITY.LOW]: "#21A600",
    };

    const statusColors = {
        [STATUS.PENDING]: "#FFA500", // orange
        [STATUS.INPROGRESS]: "#007BFF", // blue
        [STATUS.COMPLETED]: "#21A600", // green
        [STATUS.CANCELLED]: "#6C757D",
    };

    const formattedData = data.map((task) => ({
        ...task,
        statusLabel: getLabelByValue(task.status, statusOptions),
        priorityLabel: getLabelByValue(task.priority, priorityOptions),

        // Colors
        priorityColor: priorityMap[task.priority] || "#6C757D", // default gray
        statusColor: statusColors[task.status] || "#6C757D", // default gray

        // Icons
        taskTypeIcon: bug,

        // Formatted dates
        formattedActualDeadline: task.actualDeadlineDate
            ? dayjs(task.actualDeadlineDate).format("DD/MM/YYYY")
            : "-",
        formattedClientDeadline: task.clientDeadlineDate
            ? dayjs(task.clientDeadlineDate).format("DD/MM/YYYY")
            : "-",

        // Assignee name
        assignedToName:
            task?.assignee_info?.firstName && task?.assignee_info?.lastName
                ? `${task.assignee_info.firstName} ${task.assignee_info.lastName}`
                : "unassigned",
    }));

    const navigate = useNavigate();

    const handleView = useCallback((task) => {
        setSelectedTask(task);
        setDrawerOpen(true);
        navigate(`/home/taskDetails?taskId=${task._id}`, {
            state: {
                selectedMonth: queryParam.month,
                selectedYear: queryParam.year,
            },
        });
    }, [navigate, queryParam.month, queryParam.year]);

    return (
        <>
            {loading ? (
                <Box sx={{ padding: "20px" }}>
                    {[1, 2, 3, 4, 5].map((item) => (
                        <Card
                            key={item}
                            sx={{
                                marginBottom: 2,
                                padding: 2,
                                border: "1px solid #E5E9F1",
                                borderRadius: "10px",
                            }}
                        >
                            <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Skeleton variant="text" width="30%" height={24} />
                                    <Skeleton variant="circular" width={24} height={24} />
                                </Stack>
                                <Skeleton variant="text" width="80%" height={20} />
                                <Skeleton variant="text" width="60%" height={20} />
                                <Stack direction="row" spacing={1} mt={1}>
                                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
                                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
                                </Stack>
                            </Stack>
                        </Card>
                    ))}
                </Box>
            ) : (
                <MasterCommonTable
                    tableConfig={tableConfig}
                    data={formattedData}
                    columns={columns}
                    tableTotalCount={tableTotalCount}
                    handleView={handleView}
                />
            )}
        </>
    );
};

const BugManagement = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { canAdd, canEdit, canDelete } = usePermissions();
    const filteredMonthState = location.state?.selectedMonth || null;
    const filteredYearState = location.state?.selectedYear || null;
    const [taskForList, setTaskForList] = useState({});
    const [value, setValue] = useState("one");
    const [isOverAllAdmin, setIsOverAllAdmin] = useState(false);
    const [isManager, setIsManager] = useState(false);

    // Map status values to display labels for tasks state
    const [tasks, setTasks] = useState({
        [STATUS.TODO]: [],
        [STATUS.INPROGRESS]: [],
        [STATUS.QA]: [],
        [STATUS.PRODUCTION]: [],
        [STATUS.STAGING]: [],
        [STATUS.PENDING]: [],
        [STATUS.UAT]: [],
    });

    const [tableTotalCount, settableTotalCount] = useState(10);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
    const [projectData, setProjectData] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [userData, setUserData] = useState([]);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({
        Priority: [],
        Status: [],
    });

    const [tempFilters, setTempFilters] = useState({
        Priority: [],
        Status: [],
    });

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState({
        [STATUS.PENDING]: false,
        [STATUS.TODO]: true,
        [STATUS.INPROGRESS]: true,
        [STATUS.QA]: true,
        [STATUS.UAT]: true,
        [STATUS.PRODUCTION]: false,
        [STATUS.STAGING]: true,
    });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

    // Pagination state for board view - tracks visible items per column
    const INITIAL_ITEMS_PER_COLUMN = 30;
    const [visibleItems, setVisibleItems] = useState({
        [STATUS.TODO]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.INPROGRESS]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.QA]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.PRODUCTION]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.STAGING]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.PENDING]: INITIAL_ITEMS_PER_COLUMN,
        [STATUS.UAT]: INITIAL_ITEMS_PER_COLUMN,
    });

    const currentDate = dayjs();
    const { accessToken } = useAuth();

    const getUserIdFromToken = () => {
        if (!accessToken) return null;
        try {
            const decoded = jwtDecode(accessToken);
            return decoded.id;
        } catch (error) {
            console.error("Invalid token", error);
            return null;
        }
    };

    const userId = getUserIdFromToken();

    const [queryParam, setQueryParam] = useState({
        project: null,
        assignedTo: null,
        assignee: null,
        type: ISSUETYPE.BUG, // Filter only bugs
        search: null,
        client: null,
        month: filteredMonthState || currentDate.month() + 1,
        year: filteredYearState || currentDate.year(),
        status: null,
        priority: null,
    });

    const [tempDate, setTempDate] = useState({
        month: queryParam.month,
        year: queryParam.year,
    });

    const [isDateFiltered, setIsDateFiltered] = useState(false);
    const [loading, setLoading] = useState(false);

    const GetProjectData = async (employeeId = null, clientId = null) => {
        try {
            const params = {
                isEnabled: true,
                ...(employeeId && { employeeId }),
                ...(clientId && { clientId }),
            };

            const res = await getProject(params);
            if (res.data.data && Array.isArray(res.data.data)) {
                const configData = res.data.data.map((e) => ({
                    ...e,
                    label: e.name,
                }));
                setProjectData(configData);
            }
        } catch (error) {
            console.error("Error fetching project data:", error);
        }
    };

    const getTaskData = async () => {
        try {
            const queryParams = {
                isBugManager: true,
                project: queryParam.project,
                assignee: queryParam.assignee,
                assignedTo: queryParam.assignedTo,
                search: queryParam.search,
                type: ISSUETYPE.BUG, // Only fetch bugs
                month: queryParam.month.toString(),
                year: queryParam.year.toString(),
                status: queryParam.status,
                priority: queryParam.priority,
                viewType: value, // Send current tab: "one" = Board, "two" = List
            };
            const res = await getTask(queryParams);
            const taskData = res.data?.data;
            settableTotalCount(res?.data?.totalCount);

            // API now returns tasks already grouped by status
            // No need for manual grouping - just use the data directly
            setTaskForList(taskData);
            setTasks(taskData);
        } catch (error) {
            console.error("Error fetching bugs:", error);
        }
    };
    const fetchTasks = async () => {
        try {
            setLoading(true);
            const res = await getTokenbyData();
            const { isOverAllAdmin, isClient, isManager } = res.data || {};
            setIsOverAllAdmin(isOverAllAdmin);
            setIsManager(isManager);
            if (isOverAllAdmin) {
                await getTaskData();
            } else if (isClient) {
                await GetProjectData(userId);
                await getClientTaskList(userId);
            } else if (isManager) {
                await getTaskData();
            } else {
                await getTaskDataUser();
            }
        } catch (error) {
            console.error("Error fetching role data:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDeadline = (deadlineDate, status) => {
        const completedStatuses = [STATUS.QA, STATUS.PRODUCTION, STATUS.STAGING, STATUS.UAT];
        if (completedStatuses.includes(status)) {
            return { text: "Completed", color: "#4CAF50", showIcon: false };
        }

        const today = new Date();
        const deadline = new Date(deadlineDate);

        today.setHours(0, 0, 0, 0);
        deadline.setHours(0, 0, 0, 0);

        const diffTime = deadline - today;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
            return { text: `${diffDays}d left`, color: "green" };
        } else if (diffDays === 0) {
            return { text: "Today", color: "orange" };
        } else {
            return { text: `- ${Math.abs(diffDays)}day`, color: "red" };
        }
    };

    const getTaskDataUser = async () => {
        try {
            const queryParams = {
                isBugManager: true,
                project: queryParam.project,
                assignedTo: queryParam.assignedTo || userId,
                search: queryParam.search,
                type: ISSUETYPE.BUG, // Only fetch bugs
                assignee: queryParam.assignee,
                month: queryParam.month.toString(),
                year: queryParam.year.toString(),
                status: queryParam.status,
                priority: queryParam.priority,
            };
            const res = await getTaskById(queryParams);
            const taskData = res?.data?.data;

            if (Array.isArray(taskData)) {
                const updatedTasks = {
                    [STATUS.TODO]: [],
                    [STATUS.INPROGRESS]: [],
                    [STATUS.QA]: [],
                    [STATUS.PRODUCTION]: [],
                    [STATUS.STAGING]: [],
                    [STATUS.PENDING]: [],
                    [STATUS.UAT]: [],
                };

                taskData.forEach((task) => {
                    if (updatedTasks[task.status]) {
                        updatedTasks[task.status].push(task);
                    }
                });

                setTasks(updatedTasks);
            } else {
                setTasks(taskData || {
                    [STATUS.TODO]: [],
                    [STATUS.INPROGRESS]: [],
                    [STATUS.QA]: [],
                    [STATUS.PRODUCTION]: [],
                    [STATUS.STAGING]: [],
                    [STATUS.PENDING]: [],
                    [STATUS.UAT]: [],
                });
            }
        } catch (error) {
            console.error("Error fetching user bugs:", error);
        }
    };

    const getTaskDataManager = async () => {
        try {
            const queryParams = {
                isBugManager: true,
                project: queryParam.project,
                assignedTo: queryParam.assignedTo,
                search: queryParam.search,
                type: ISSUETYPE.BUG, // Only fetch bugs
                month: queryParam.month.toString(),
                year: queryParam.year.toString(),
                status: queryParam.status,
                priority: queryParam.priority,
            };

            const res = await getTask(queryParams);
            const taskData = res.data?.data || {};
            settableTotalCount(res?.data?.totalCount);

            // Apply manager filtering to each status group
            const filteredTasks = {};
            Object.keys(taskData).forEach((status) => {
                // Ensure taskData[status] is an array before filtering
                if (Array.isArray(taskData[status])) {
                    filteredTasks[status] = taskData[status].filter((task) => {
                        if (task.status === STATUS.PENDING) {
                            return true;
                        }
                        return task.assignee_info?._id === userId;
                    });
                } else {
                    filteredTasks[status] = [];
                }
            });

            setTaskForList(filteredTasks);
            setTasks(filteredTasks);
        } catch (error) {
            console.error("Error fetching bugs:", error);
        }
    };

    // Memoize filtered status options to prevent recalculation on every render
    const filteredStatusOptions = useMemo(() => {
        if (!isOverAllAdmin) {
            if (isManager) {
                return statusOptions;
            } else {
                return statusOptions.filter(
                    (option) =>
                        option.value !== STATUS.PENDING && option.value !== STATUS.UAT
                );
            }
        }
        return statusOptions;
    }, [isOverAllAdmin, isManager]);

    // Handle column visibility toggle
    const handleColumnMenuOpen = (event) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnToggle = (statusValue) => {
        setVisibleColumns(prev => ({
            ...prev,
            [statusValue]: !prev[statusValue]
        }));
    };

    // Filter status options based on visible columns
    const visibleStatusOptions = useMemo(() => {
        return filteredStatusOptions.filter(option => visibleColumns[option.value]);
    }, [filteredStatusOptions, visibleColumns]);

    const getUserData = async () => {
        try {
            const res = await getUser({ isEnabled: true });
            const configData = res.data.data
                .filter((e) => !e.isOverAllAdmin)
                .map((e) => ({
                    ...e,
                    label: e.name,
                }));
            setUserData(configData);
        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [queryParam, value]); // Added 'value' to refetch when tab changes

    useEffect(() => {
        getUserData();
        GetProjectData();
    }, []);

    const debouncedHandleSearch = useCallback(
        debounce((searchText) => {
            setQueryParam((prevParams) => ({
                ...prevParams,
                search: searchText,
            }));
        }, 300),
        []
    );

    const handleView = (task) => {
        setSelectedTask(task);
        setDrawerOpen(true);
        navigate(`/home/taskDetails?taskId=${task._id}`, {
            state: {
                selectedMonth: queryParam.month,
                selectedYear: queryParam.year,
                isOverAllAdmin: isOverAllAdmin,
                isManager: isManager,
            },
        });
    };

    const handleSearch = (searchText) => {
        debouncedHandleSearch(searchText);
    };

    const handleAddTaskClick = () => {
        setAddTaskModalOpen(true);
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setDrawerOpen(true);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleProjectChange = (newValue) => {
        setQueryParam((prevParams) => ({
            ...prevParams,
            project: newValue?._id || null,
        }));
        setSelectedProject(newValue?._id || null);
    };

    const handleEmployeeChange = (newValue) => {
        setQueryParam((prevParams) => ({
            ...prevParams,
            assignee: newValue?._id || null,
        }));
        setSelectedEmployee(newValue);
    };

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        // Safety check: ensure source and destination columns exist
        if (!tasks[source.droppableId] || !tasks[destination.droppableId]) {
            console.error("Invalid source or destination column");
            return;
        }

        const sourceColumn = [...tasks[source.droppableId]];
        const destColumn =
            destination.droppableId in tasks
                ? [...tasks[destination.droppableId]]
                : [];

        const [movedTask] = sourceColumn.splice(source.index, 1);

        if (source.droppableId !== destination.droppableId) {
            movedTask.status = parseInt(destination.droppableId);
            destColumn.splice(destination.index, 0, movedTask);
        } else {
            sourceColumn.splice(destination.index, 0, movedTask);
        }

        setTasks({
            ...tasks,
            [source.droppableId]: sourceColumn,
            [destination.droppableId]: destColumn,
        });

        if (source.droppableId !== destination.droppableId) {
            try {
                const formData = new FormData();
                const payload = {
                    status: parseInt(destination.droppableId),
                    StatusChange: true,
                    assignedToId: userId,
                };
                formData.append("bodyData", JSON.stringify(payload));
                const response = await updateTask(draggableId, formData);
                if (response.error === false) {
                    ToastSuccess(response.data.message);
                }
            } catch (error) {
                console.error("Error updating bug status:", error);
                // Revert the state on error
                const errorMsg =
                    error?.data?.data?.message || error.message || "An error occurred";
                ToastError(errorMsg);
                setTasks(tasks);
            }
        }
    };

    const truncateText = (text, maxLength = 30) => {
        return text
            ? text.length > maxLength
                ? `${text.substring(0, maxLength)}...`
                : text
            : "-";
    };

    const handleFilterClick = () => {
        setTempFilters(selectedFilters);
        setOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setOpenDrawer(false);
    };

    const handleDateChange = (e) => {
        const selectedDate = dayjs(e?.$d);
        setQueryParam({
            ...queryParam,
            month: selectedDate.month() + 1,
            year: selectedDate.year() || queryParam.year,
        });
    };

    const handleRemoveFilter = (filterType, valueToRemove) => {
        setTempFilters((prev) => ({
            ...prev,
            [filterType]: prev[filterType].filter(
                (val) => val.value !== valueToRemove.value
            ),
        }));
    };

    const handleApplyFilters = () => {
        setSelectedFilters(tempFilters);
        const isNewDateFiltered =
            tempDate.month !== (filteredMonthState || currentDate.month() + 1) ||
            tempDate.year !== (filteredYearState || currentDate.year());

        setIsDateFiltered(isNewDateFiltered);
        setQueryParam({
            ...queryParam,
            month: tempDate.month,
            year: tempDate.year,
            status:
                tempFilters.Status.length > 0
                    ? tempFilters.Status.map((s) => s.value).join(",")
                    : null,
            priority:
                tempFilters.Priority.length > 0
                    ? tempFilters.Priority.map((p) => p.value).join(",")
                    : null,
        });
        setOpenDrawer(false);
    };

    const handleClearAll = async () => {
        setTempFilters({
            Priority: [],
            Status: [],
        });

        const defaultMonth = currentDate.month() + 1;
        const defaultYear = currentDate.year();

        setTempDate({
            month: defaultMonth,
            year: defaultYear,
        });

        setQueryParam({
            ...queryParam,
            month: defaultMonth,
            year: defaultYear,
            status: null,
            priority: null,
            type: ISSUETYPE.BUG,
            assignedTo: null,
            assignee: null,
            search: null,
            project: null,
        });

        setSelectedFilters({
            Priority: [],
            Status: [],
        });

        setIsDateFiltered(false);
        setSelectedProject(null);
        setSelectedEmployee(null);

        try {
            await getTask({
                isBugManager: true,
                month: defaultMonth.toString(),
                year: defaultYear.toString(),
                type: ISSUETYPE.BUG,
            });
        } catch (error) {
            console.error("Error fetching bugs after clear:", error);
        }
    };

    const filterOptions = [
        { title: "Priority", options: priorityOptions },
        { title: "Status", options: statusOptions },
    ];

    const renderTaskCard = (task, index) => {
        const priorityLabel = getLabelByValue(task.priority, priorityOptions);
        const statusLabel = getLabelByValue(task.status, statusOptions);
        return (
            <Draggable key={task._id} draggableId={task._id} index={index}>
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => handleView(task)}
                        style={{
                            ...provided.draggableProps.style,
                        }}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                boxShadow: "none",
                                border: task?.clientVerifyStatus == 1 ? "2px solid orange" : "2px solid #E5E9F1",
                                paddingBottom: "0px",
                                height: "100%",
                                borderRadius: "10px",
                            }}
                        >
                            <CardContent sx={{ paddingBottom: "15px !important" }}>
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: "6px",
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            gap: "3px",
                                        }}
                                    >
                                        <IoFlagOutline
                                            size={16}
                                            color={
                                                task.priority === PRIORITY.HIGH
                                                    ? "#D50707" // Red
                                                    : task.priority === PRIORITY.MEDIUM
                                                        ? "#D7BB21" // Yellow
                                                        : "#21A600" // Green
                                            }
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                fontFamily: "Inter, sans-serif",
                                                marginLeft: "4px",
                                                color: "#000000",
                                            }}
                                        >
                                            {task?.taskNo}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={"row"} sx={{ alignItems: "center" }} spacing={0.4}>
                                        <img
                                            src={bug}
                                            style={{ width: "20px", height: "20px" }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                fontFamily: "Inter, sans-serif",
                                            }}
                                        >
                                            Bug
                                        </Typography>
                                        {task.relatedTask && (
                                            <Tooltip title="Go To Task">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/home/taskDetails?taskId=${task.relatedTask}`, {
                                                            state: {
                                                                selectedMonth: queryParam.month,
                                                                selectedYear: queryParam.year,
                                                                isOverAllAdmin: isOverAllAdmin,
                                                                isManager: isManager,
                                                            },
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: "2px",
                                                        color: "#055253",
                                                        "&:hover": {
                                                            backgroundColor: "#e7f5f6",
                                                        },
                                                    }}
                                                >
                                                    <LaunchIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Stack>
                                <Stack>
                                    <Divider
                                        style={{
                                            margin: "0px",
                                            border: "0.6px solid #E5E9F1",
                                        }}
                                    />
                                </Stack>
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginTop: "6px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "15px",
                                            fontWeight: 500,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            width: task.isHold ? "calc(100% - 30px)" : "100%",
                                            fontFamily: "Inter, sans-serif",
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {task?.title}
                                    </Typography>
                                    {task.isHold && (
                                        <Tooltip title="On Hold">
                                            <Box
                                                sx={{
                                                    width: "fit-content",
                                                    padding: "4px",
                                                    height: "24px",
                                                    backgroundColor: "#868d8dff",
                                                    borderRadius: "10px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    letterSpacing: "0.6px",
                                                    color: "white",
                                                    fontSize: "12px",
                                                    fontWeight: "bold",
                                                    fontFamily: "Inter, sans-serif",
                                                }}
                                            >
                                                Hold
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Stack>

                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#687280",
                                        fontWeight: 400,
                                        fontFamily: "Inter, sans-serif",
                                        mt: "3px",
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {truncateText(
                                        task?.description.replace(/<\/?[^>]+(>|$)/g, "")
                                    )}
                                </Typography>

                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        gap: "6px",
                                        justifyContent: "space-between",
                                        marginTop: "10px",
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            gap: "6px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                padding: "2px 4px",
                                                border: "1px solid #E5E9F1",
                                                borderRadius: "3px",
                                                alignItems: "center",
                                                gap: "3px",
                                                backgroundColor: "#FBFBFB",
                                            }}
                                        >
                                            <ImAttachment />
                                            <Typography fontSize={"12px"}>
                                                {task?.file?.length || 0}
                                            </Typography>
                                        </Stack>
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                padding: "2px 4px",
                                                border: "1px solid #E5E9F1",
                                                borderRadius: "3px",
                                                alignItems: "center",
                                                gap: "3px",
                                                backgroundColor: "#FBFBFB",
                                            }}
                                        >
                                            <LuMessageSquareText />
                                            <Typography fontSize={"12px"}>
                                                {task?.comments?.length || 0}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            position: "relative",
                                            gap: "10px",
                                        }}
                                    >
                                        {/* Priority Chip */}
                                        <Chip
                                            label={
                                                task.priority === PRIORITY.HIGH
                                                    ? "High"
                                                    : task.priority === PRIORITY.MEDIUM
                                                        ? "Medium"
                                                        : "Low"
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor:
                                                    task.priority === PRIORITY.HIGH
                                                        ? "#FFE5E5"
                                                        : task.priority === PRIORITY.MEDIUM
                                                            ? "#FFF4D6"
                                                            : "#E0F7E9",
                                                color:
                                                    task.priority === PRIORITY.HIGH
                                                        ? "#E63946"
                                                        : task.priority === PRIORITY.MEDIUM
                                                            ? "#F4A261"
                                                            : "#2A9D8F",
                                                fontWeight: 600,
                                                fontSize: "12px",
                                                height: "24px",
                                            }}
                                        />

                                        <Tooltip
                                            title={
                                                task?.assignee_info
                                                    ? `${task?.assignee_info?.firstName} ${task?.assignee_info?.lastName}`
                                                    : "Unassigned"
                                            }
                                        >
                                            <Avatar
                                                sx={{
                                                    width: "30px",
                                                    height: "30px",
                                                    fontSize: "12px",
                                                    backgroundColor: "#C7EEEF",
                                                    color: task?.assignee_info ? "#055253" : "inherit",
                                                    fontWeight: 500,
                                                }}
                                                alt={
                                                    task?.assignee_info
                                                        ? `${task?.assignee_info?.firstName} ${task?.assignee_info?.lastName}`
                                                        : "Profile"
                                                }
                                                src={
                                                    task?.assignee_info?.file?.location
                                                        ? task.assignee_info.file.location
                                                        : task?.assignedToProfile
                                                            ? task.assignedToProfile
                                                            : "/static/images/avatar/1.jpg"
                                                }
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            >
                                                {task?.assignee_info ? (
                                                    `${task?.assignee_info?.firstName?.[0] || ""}${task?.assignee_info?.lastName?.[0] || ""
                                                        }`.toUpperCase()
                                                ) : (
                                                    <PermIdentityOutlinedIcon style={{ color: "#055253" }} />
                                                )}
                                            </Avatar>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </Draggable>
        );
    };

    const handleLoadMore = useCallback((status) => {
        setVisibleItems(prev => ({
            ...prev,
            [status]: prev[status] + 20
        }));
    }, []);



    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Grid sx={{ height: "100vh", overflowY: "auto", scrollbarWidth: "none" }}>
                <Stack marginTop={1} sx={{ padding: { xs: "10px", md: "0px 20px" } }}>
                    <Typography sx={{ fontSize: "16px" }}>All Bugs</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#687280" }}>
                        View, assign and manage all bugs—track deadlines, priorities, and
                        status updates.
                    </Typography>

                    <Grid
                        container
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        mt={2}
                    >
                        <Grid item xs={12} md={2.5} sx={{ mb: { xs: 2, md: 0 } }}>
                            <Box
                                sx={{
                                    width: { xs: "100%", md: "100%" },
                                    border: "1px solid #D5DBDE",
                                    padding: "5px",
                                    borderRadius: "6px",
                                }}
                            >
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    textColor="black"
                                    indicatorColor="transparent"
                                    aria-label="secondary tabs example"
                                    sx={{
                                        minHeight: "24px !important",
                                        padding: "0px !important",
                                        width: "100%",
                                    }}
                                >
                                    <Tab
                                        value="one"
                                        label="Board"
                                        icon={<MdOutlineSpaceDashboard />}
                                        iconPosition="start"
                                        sx={{
                                            color: "black",
                                            backgroundColor:
                                                value === "one" ? "#E5E9F1" : "transparent",
                                            borderRadius: "6px",
                                            padding: "10px !important",
                                            height: "10px !important",
                                            minHeight: "30px !important",
                                            fontSize: "12px",
                                            width: "50%",
                                        }}
                                    />
                                    <Tab
                                        value="two"
                                        label="List"
                                        icon={<FaListUl />}
                                        iconPosition="start"
                                        sx={{
                                            color: "black",
                                            backgroundColor:
                                                value === "two" ? "#E5E9F1" : "transparent",
                                            borderRadius: "6px",
                                            padding: "0px !important",
                                            height: "10px !important",
                                            minHeight: "30px !important",
                                            fontSize: "12px",
                                            width: "50%",
                                        }}
                                    />
                                </Tabs>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9.5}>
                            <Grid
                                container
                                spacing={{ xs: 1, md: 2 }}
                                justifyContent={"center"}
                                alignItems={"center"}
                            >
                                <Grid item xs={12} sm={6} md={2.5}>
                                    <Stack>
                                        <Box
                                            className="SearchInputBoxContainer"
                                            sx={{ margin: 0, paddingLeft: 0 }}
                                        >
                                            <input
                                                className="SearchInputBox"
                                                placeholder="Search Here"
                                                onChange={(e) => handleSearch(e.target.value)}
                                                style={{ backgroundColor: "#f7f7f7", paddingRight: "55px" }}
                                            />
                                            <SearchOutlinedIcon className="Search_Icon" />
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6} sm={6} md={2.5}>
                                    <Autocomplete
                                        options={projectData}
                                        getOptionLabel={(option) => option?.label || ""}
                                        value={
                                            projectData.find(
                                                (proj) => proj?._id === selectedProject
                                            ) || null
                                        }
                                        onChange={(event, newValue) =>
                                            handleProjectChange(newValue)
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Select a Project" />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6} md={2}>
                                    <Autocomplete
                                        options={userData}
                                        getOptionLabel={(option) => option?.label || ""}
                                        value={selectedEmployee}
                                        onChange={(event, newValue) =>
                                            handleEmployeeChange(newValue)
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Select Employee" />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6} md={1.5}>
                                    <Stack
                                        component="div"
                                        sx={{
                                            border: "1px solid #D5DBDE",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "44px",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            flexDirection: "row-reverse",
                                            gap: "10px",
                                            padding: "0 12px",
                                            position: "relative",
                                        }}
                                        onClick={handleFilterClick}
                                    >
                                        <Typography>Filter</Typography>

                                        <IoFilterSharp size={20} />
                                        {(Object.values(selectedFilters).flat().length > 0 ||
                                            isDateFiltered) && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: -5,
                                                        top: -5,
                                                        backgroundColor: "#008080",
                                                        color: "white",
                                                        borderRadius: "50%",
                                                        width: 20,
                                                        height: 20,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "11px",
                                                    }}
                                                >
                                                    {Object.values(selectedFilters).flat().length +
                                                        (isDateFiltered ? 1 : 0)}
                                                </Box>
                                            )}
                                    </Stack>
                                    <Drawer
                                        anchor="right"
                                        open={openDrawer}
                                        onClose={handleCloseDrawer}
                                        sx={{
                                            zIndex: 1500, // Ensure Drawer is above Header (1300)
                                            "& .MuiDrawer-paper": {
                                                width: "350px",
                                                padding: "20px",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 3,
                                            }}
                                        >
                                            <Stack flexDirection={"row"} gap={1}>
                                                <img src={filterIcon1} />
                                                <Typography variant="h6">Filters</Typography>
                                            </Stack>
                                            <Stack>
                                                <IoClose
                                                    size={24}
                                                    onClick={handleCloseDrawer}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </Stack>
                                        </Box>

                                        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                                            {/* <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                Date
                                            </Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DemoContainer components={["DatePicker"]}>
                                                    <DatePicker
                                                        views={["year", "month"]}
                                                        openTo="month"
                                                        value={dayjs(
                                                            `${tempDate.year}-${tempDate.month}-01`
                                                        )}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setTempDate({
                                                                    month: newValue.month() + 1,
                                                                    year: newValue.year(),
                                                                });
                                                            }
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "outlined",
                                                            },
                                                        }}
                                                        slots={{
                                                            openPickerIcon: CalendarTodayRoundedIcon,
                                                        }}
                                                    />
                                                </DemoContainer>
                                            </LocalizationProvider> */}
                                            {filterOptions.map((filter, index) => (
                                                <Box key={index} sx={{ my: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                        {filter.title}
                                                    </Typography>
                                                    <Autocomplete
                                                        multiple
                                                        options={filter.options}
                                                        getOptionLabel={
                                                            filter.getOptionLabel ||
                                                            ((option) => option.label)
                                                        }
                                                        value={tempFilters[filter.title] || []}
                                                        onChange={(event, newValue) => {
                                                            setTempFilters((prev) => ({
                                                                ...prev,
                                                                [filter.title]: newValue,
                                                            }));
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder={`Select ${filter.title}`}
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    "&  .MuiOutlinedInput-root .MuiAutocomplete-input":
                                                                    {
                                                                        paddingTop: "0px",
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        renderTags={() => null}
                                                        fullWidth
                                                        disablePortal
                                                    />
                                                    {tempFilters[filter.title]?.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                gap: 1,
                                                                mt: 1,
                                                                p: 1,
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            {tempFilters[filter.title].map((value, idx) => (
                                                                <Chip
                                                                    key={idx}
                                                                    label={
                                                                        filter.getOptionLabel
                                                                            ? filter.getOptionLabel(value)
                                                                            : value.label
                                                                    }
                                                                    onDelete={() =>
                                                                        handleRemoveFilter(filter.title, value)
                                                                    }
                                                                    deleteIcon={<IoClose size={14} />}
                                                                    size="small"
                                                                    sx={{
                                                                        padding: "5px",
                                                                        backgroundColor: "#C7EEEF",
                                                                        "& .MuiChip-deleteIcon": {
                                                                            color: "#616161",
                                                                            "&:hover": { color: "#212121" },
                                                                        },
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>

                                        <Stack
                                            flexDirection={"row"}
                                            justifyContent={"space-between"}
                                        >
                                            <Stack
                                                onClick={handleClearAll}
                                                sx={{
                                                    border: "1px solid #DDDDDD",
                                                    padding: "8px 10px",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        color: "black",
                                                    }}
                                                >
                                                    Clear All
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                onClick={handleApplyFilters}
                                                sx={{
                                                    backgroundColor: "#008080",
                                                    padding: "8px 10px",
                                                    borderRadius: "6px",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        color: "white",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    Apply
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Drawer>
                                </Grid>
                                {value === "one" && (
                                    <Grid item xs="auto">
                                        <Tooltip title="Show/Hide Columns">
                                            <IconButton
                                                onClick={handleColumnMenuOpen}
                                                sx={{
                                                    border: "1px solid #D5DBDE",
                                                    borderRadius: "6px",
                                                    padding: "10px",
                                                }}
                                            >
                                                <TableChartIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            anchorEl={columnMenuAnchor}
                                            open={Boolean(columnMenuAnchor)}
                                            onClose={handleColumnMenuClose}
                                            PaperProps={{
                                                sx: {
                                                    mt: 1,
                                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                                    borderRadius: "8px",
                                                    minWidth: "200px",
                                                },
                                            }}
                                        >
                                            <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #E5E9F1" }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
                                                    Show/Hide Columns
                                                </Typography>
                                            </Box>
                                            {filteredStatusOptions.map((option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    onClick={() => handleColumnToggle(option.value)}
                                                    sx={{
                                                        py: 1,
                                                        px: 2,
                                                        "&:hover": {
                                                            backgroundColor: "#F5F5F5",
                                                        },
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={visibleColumns[option.value]}
                                                                size="small"
                                                                sx={{
                                                                    color: "#008080",
                                                                    "&.Mui-checked": {
                                                                        color: "#008080",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{ fontSize: "14px" }}>
                                                                {option.label}
                                                            </Typography>
                                                        }
                                                        sx={{ margin: 0, width: "100%" }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Grid>
                                )}
                                <Grid item xs={6} sm={6} md={1.5}>
                                    <PermissionGate module="taskManagement" action="add">
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                backgroundColor: "#008080",
                                                borderRadius: "6px",
                                                minHeight: "36px",
                                                height: "44px",
                                                cursor: "pointer",
                                            }}
                                            onClick={handleAddTaskClick}
                                        >
                                            <GoPlus size={30} color="white" />
                                            <Typography sx={{ color: "white" }}>Add Bug</Typography>
                                        </Stack>
                                    </PermissionGate>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Stack>
                <Divider />
                {value === "one" ? (
                    <Box
                        sx={{
                            display: "flex",
                            overflowX: "auto",
                            padding: "0px 0",
                            gap: "16px",
                        }}
                    >
                        {visibleStatusOptions.map((statusOption) => (
                            <Box
                                sx={{ flex: "0 0 320px", minWidth: "320px" }}
                                key={statusOption.value}
                            >
                                <Droppable droppableId={statusOption.value.toString()}>
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps}>
                                            <Stack
                                                sx={{
                                                    padding: "10px",
                                                    backgroundColor: "#FBFBFB",
                                                    borderRadius: "10px 10px 0 0",
                                                    border: "1px solid #E5E9F1",
                                                }}
                                            >
                                                <Stack
                                                    sx={{
                                                        border: "1px solid #E5E9F1",
                                                        borderRadius: "6px",
                                                        padding: "6px 8px",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Stack
                                                        sx={{
                                                            flexDirection: "row",
                                                            gap: "10px",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography variant="h6" sx={{ fontSize: 16 }}>
                                                            {statusOption.label}
                                                        </Typography>
                                                        <Stack
                                                            sx={{
                                                                backgroundColor:
                                                                    statusOption.value === STATUS.TODO
                                                                        ? "#FF87D726"
                                                                        : statusOption.value === STATUS.INPROGRESS
                                                                            ? "#9D87FF26"
                                                                            : statusOption.value === STATUS.QA
                                                                                ? "#FFBD4C26"
                                                                                : "#2AD10026",
                                                                flexDirection: "row",
                                                                alignItems: "center",
                                                                padding: "2px 8px",
                                                                borderRadius: "16px",
                                                                gap: "2px",
                                                            }}
                                                        >
                                                            {statusOption.value === STATUS.INPROGRESS && (
                                                                <img
                                                                    src={InProgress_Icon}
                                                                    alt=""
                                                                    width={"20px"}
                                                                    height={"20px"}
                                                                />
                                                            )}
                                                            {statusOption.value === STATUS.QA && (
                                                                <img
                                                                    src={QA_Icon}
                                                                    alt=""
                                                                    width={"20px"}
                                                                    height={"20px"}
                                                                />
                                                            )}
                                                            {(statusOption.value === STATUS.PRODUCTION ||
                                                                statusOption.value === STATUS.UAT) && (
                                                                    <img
                                                                        src={Completed_Icon}
                                                                        alt=""
                                                                        width={"20px"}
                                                                        height={"20px"}
                                                                    />
                                                                )}
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    fontWeight: 500,
                                                                    color:
                                                                        statusOption.value === STATUS.TODO
                                                                            ? "#FF87D7"
                                                                            : statusOption.value === STATUS.INPROGRESS
                                                                                ? "#9D87FF"
                                                                                : statusOption.value === STATUS.QA
                                                                                    ? "#FFBD4C"
                                                                                    : "#2AD100",
                                                                }}
                                                            >
                                                                {tasks[statusOption.value]?.length || 0}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Stack
                                                spacing={1}
                                                sx={{
                                                    padding: "10px",
                                                    backgroundColor: "#FBFBFB",
                                                    borderRadius: "0px 0px 6px 6px",
                                                    border: "1px solid #E5E9F1",
                                                    height: "600px",
                                                    overflow: "scroll",
                                                    marginBottom: "60px",
                                                    "&::-webkit-scrollbar": {
                                                        display: "none",
                                                    },
                                                }}
                                            >
                                                {loading ? (
                                                    <Stack spacing={1}>
                                                        {[1, 2, 3].map((item) => (
                                                            <Card
                                                                key={item}
                                                                elevation={0}
                                                                sx={{
                                                                    border: "2px solid #E5E9F1",
                                                                    borderRadius: "10px",
                                                                    padding: "12px",
                                                                }}
                                                            >
                                                                <Stack spacing={1}>
                                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                        <Skeleton variant="rectangular" width={60} height={16} sx={{ borderRadius: "4px" }} />
                                                                        <Skeleton variant="circular" width={18} height={18} />
                                                                    </Stack>
                                                                    <Skeleton variant="text" width="90%" height={18} />
                                                                    <Skeleton variant="text" width="70%" height={14} />
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: "4px" }} />
                                                                        <Skeleton variant="text" width={90} height={14} />
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                        <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: "12px" }} />
                                                                        <Skeleton variant="circular" width={28} height={28} />
                                                                    </Stack>
                                                                </Stack>
                                                            </Card>
                                                        ))}
                                                    </Stack>
                                                ) : (
                                                    <Stack spacing={2}>
                                                        {tasks[statusOption.value]
                                                            ?.slice(0, visibleItems[statusOption.value])
                                                            .map((task, index) =>
                                                                renderTaskCard(task, index)
                                                            )}
                                                        {tasks[statusOption.value]?.length > visibleItems[statusOption.value] && (
                                                            <Button
                                                                onClick={() => handleLoadMore(statusOption.value)}
                                                                sx={{
                                                                    width: "100%",
                                                                    mt: 1,
                                                                    py: 1,
                                                                    color: "#055253",
                                                                    borderColor: "#C7EEEF",
                                                                    "&:hover": {
                                                                        backgroundColor: "#E7F5F6",
                                                                        borderColor: "#055253",
                                                                    },
                                                                }}
                                                                variant="outlined"
                                                                size="small"
                                                            >
                                                                Load More ({tasks[statusOption.value].length - visibleItems[statusOption.value]} remaining)
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                )}
                                                {provided.placeholder}
                                            </Stack>
                                        </div>
                                    )}
                                </Droppable>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <BugList
                        tasks={tasks}
                        tableTotalCount={tableTotalCount}
                        setSelectedTask={setSelectedTask}
                        setDrawerOpen={setDrawerOpen}
                        queryParam={queryParam}
                        loading={loading}
                    />
                )}
            </Grid>

            {addTaskModalOpen && (
                <AddTaskModal
                    open={addTaskModalOpen}
                    onClose={() => {
                        setAddTaskModalOpen(false);
                        fetchTasks();
                    }}
                    defaultIssueType={ISSUETYPE.BUG}
                    formType="add"
                    fetchTasks={fetchTasks}
                />
            )}

            {drawerOpen && selectedTask && (
                <MemoizedTaskDrawer
                    open={drawerOpen}
                    onClose={() => {
                        setDrawerOpen(false);
                        setSelectedTask(null);
                        fetchTasks();
                    }}
                    task={selectedTask}
                    onTaskUpdate={fetchTasks}
                />
            )}
        </DragDropContext>
    );
};

export default BugManagement;
